// src/com/auto_plan/AutoPlanInput.js
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, Grid, CircularProgress
} from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import Time24Picker from '../../common/Time24Picker';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox, VzmPrint } from '../../common/VzmStyled';
import { useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { AutoPlanDictionary } from './AutoPlanDict';
import { userInfoRepo, vehTypeGroupOrderListByDcRepo } from '../../value/AppModel';
import ValueMisc from '../../value/ValueMisc';
import IsValid from '../../value/IsValid';

const InputMap = AutoPlanDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

const LabelStyle={
    autoIRMiddleBlueBold : {
        color:'blue',
        fontWeight:'bold',
    },
    autoIRsmall : {
        fontSize:10
    },
    autoIRSmallBold :{
        fontSize:10,
        fontWeight:'bold'
    },
    autoIRMiddle: {
        fontSize:11
    },
    autoIRMiddleRedBold:{
        color: '#ff3600',
        fontWeight:'bold'
    },
    autoIRBig:{
        //fontSize:12
    },
    autoIRBigBold:{
        //fontSize:12,
        fontWeight:'bold'
    },
    autoIrPtYellow : {
        width:'100%',
        backgroundColor:'yellow',
        color:'red',
        //fontSize:12,
        fontWeight:'bold',
        borderRadius:5,
        paddingRight:5
    },
    autoIrPtRed:{
        width:'100%',
        backgroundColor:'red',
        color:'yellow',
        //fontSize:12,
        fontWeight:'bold',
        borderRadius:5,
        paddingRight:5
    },
    autoIrPtNormal:{
        width:'100%',
        //fontSize:12,
        fontWeight:'bold',
        color:'red',
        paddingRight:5
    },
    /*자동배차 호차 */
    autoHochaChangeGood: {
        width:'100%',
        backgroundColor: 'red', // borderRadius:3,
        paddingLeft:2, paddingRight:2,
        color:'white'
    },
    autoHochaNextDay:{
        width:'100%',
        backgroundColor: '#ffd38a', padding: '0 2px 0 2px'
    },
    autoHochaTransFragY:{
        width:'100%', border: '2px solid #2962a4', padding: '0 2px 0 2px'
    },
    autoNoneStyle:{
        width:'100%', padding: '0 2px 0 2px'
    },
    rowStyle1:{
        backgroundColor: '#FAF8F8'
    },
    rowStyle2:{
        backgroundColor: '#FFFFFF'
    },
    // new
    red: {color:'red'},
    blue: {color:'blue'},
};

const BorderLightBlue = AppStyle.BorderCCC;
const PlanTable = styled('table')({
    width: '100%',
    borderCollapse: 'collapse'
});
const PlanTableHead = styled('thead')({
    position: 'sticky',
    top: 0,
    backgroundColor: '#f2f2f2',
    border: AppStyle.BorderCCC,
    boxSizing: 'border-box',
    zIndex: 1
});
const StoreRow = styled('tr')({
    border: AppStyle.BorderCCC,
    padding: 0,
});

const TitleCell = styled('td')({
    fontSize: '0.75rem',
    textAlign: 'center',
    padding: 5,
    lineHeight: '1.35rem',
    boxSizing: 'border-box',
});

const ValueCell = styled('td')({
    padding: '2px 5px 2px 5px', fontSize: '0.75rem'
});

const CC = styled('div')({
    // Cell Content
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
    padding: 0
});

const TableRow = styled('tr')({
    borderBottom: '1px solid #ccc',
    padding: 0,
});


const Boxel = ({children, style, ccStyle, onMouseEnter, onMouseLeave}) => {
    return(
        <ValueCell style={style}
            onMouseEnter={(e)=>{if(onMouseEnter) onMouseEnter(e)}}
            onMouseLeave={(e)=>{if(onMouseLeave) onMouseLeave(e)}}
        >
            <CC style={ccStyle}>
                {children}
            </CC>
        </ValueCell>
    )
};

const Cell = styled('div')({
    padding:0
});

const uncolonHhmm = (h2colonm2) => {
    if(h2colonm2) {
        return h2colonm2.replace(':', '').substr(0,4);
    }
    return '';
};

/**
 * (AUTO_PLAN) 정보 생성 또는 편집 Dialog
 * 상온 자동배차
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2026-06-17 15:52:55 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function AutoPlanInput({
    open,
    hocha,
    item,
    autoPlanUploadItem,
    onClose,
    onRequestSave,
    responseSave,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const isEdit = useMemo(()=>Boolean(item), [item]);
    // const codeTree = useReactiveVar(codeTreeRepo);
    const vehTypeGroupOrderListByDc = useReactiveVar(vehTypeGroupOrderListByDcRepo);
    const vehTypeList = useMemo(()=>{
        if(vehTypeGroupOrderListByDc.records.length > 0) {
            const list = [...vehTypeGroupOrderListByDc.records];
            list.unshift({vehTypeId:AppWord.ChooseValue, vehTypeName:AppWord.ChooseKorean});
            return list;
        }
        return [{vehTypeId:AppWord.ChooseValue, vehTypeName:AppWord.ChooseKorean}];
    }, [vehTypeGroupOrderListByDc]);
    const resOverPtStyle = useMemo(()=>{
        if(autoPlanUploadItem) {
            if(autoPlanUploadItem.restOverPt >= 3) return LabelStyle.autoIrPtYellow;
            if(autoPlanUploadItem.nextday04Pt + autoPlanUploadItem.restOverPt <= -3) return LabelStyle.autoIrPtRed;
            return LabelStyle.autoIrPtNormal;
        }
        return null;
    }, [autoPlanUploadItem]);
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSave;
    const openConfirmDialog = useConfirmDialog();
        // ##### Call GraphQL to get List #####

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            if(item) {
                inputSet.data = {...item};
                if(inputSet.data.startTime) inputSet.data.startTime = uncolonHhmm(inputSet.data.startTime);
                if(inputSet.data.arriveTime) inputSet.data.arriveTime = uncolonHhmm(inputSet.data.arriveTime);
                if(inputSet.data.shprTime) inputSet.data.shprTime = uncolonHhmm(inputSet.data.shprTime);
                if(inputSet.data.returnTime) inputSet.data.returnTime = uncolonHhmm(inputSet.data.returnTime);
            }
            else {
                inputSet.data = {...defaultInputData};
            }
            setInput(inputSet);
        }
    }, [open, item, sessionInfo]);

    const getExpectRunTime = (data) => {
        const expect = autoPlanUploadItem?.basicExpectRunTime;
        return IsValid.hhmm4(expect) ? expect : null;
    };
    /*
    Legacy code.

    
    @action onChangeStartTime=()=>{
        
        if(this.start_time_hh === ''|| this.start_time_mm ===''){return}
        const planDateStr = this.selectObj.plan_date
        // 출발시간 date 변환 
        const startDate = new Date(parseInt(planDateStr.substring(0,4)),parseInt(planDateStr.substring(4,6))-1, parseInt(planDateStr.substring(6,8)))
        let startHour = parseInt(this.start_time_hh)
        let startMin = parseInt(this.start_time_mm)
        startDate.setHours(startHour, startMin)
        // 접안시간 계산 
        const shprDate = new Date(parseInt(planDateStr.substring(0,4)),parseInt(planDateStr.substring(4,6))-1, parseInt(planDateStr.substring(6,8)))
        shprDate.setHours(startHour, startMin - 50)
        this.shpr_time_hh = Utils.makeNumCipher(shprDate.getHours().toString(),2)
        this.shpr_time_mm = Utils.makeNumCipher(shprDate.getMinutes().toString(),2)
        // 도착시간변경
        // 22:00시보다 크거나 같은 경우, 도착 시간을06:00으로 설정
        // 운송요청하고는 별개로 간단하게 구현(기존 소스 내용)
        if(startHour >= 22){
            // chokb2018-03-08 수정 (도착지정보에 등록된 출발시간 22시 이후의  아침 도착시간정보로....)
			// 도착지정보(출발요청시간,기본예상운행 시간, 도착요청시간 사용 )
            if(!this.isInit){
                this.arrive_time_hh =  this.arriveInfo.arrive_req_time_next.substring(0,2)
                this.arrive_time_mm =  this.arriveInfo.arrive_req_time_next.substring(2,4)
            }
            this.isInit = false
        }else{
            // 22시 전인 경우, 거리시간을 출발시간에 더해서 도착시간을 자동계산
            const arriveDate = new Date(parseInt(planDateStr.substring(0,4)),parseInt(planDateStr.substring(4,6))-1, parseInt(planDateStr.substring(6,8)))
            const expectTimeStr = this.basicExpectTime

            arriveDate.setHours(startHour + parseInt(expectTimeStr.substring(0,2)), startMin + parseInt(expectTimeStr.substring(2,4)))
            this.arrive_time_hh =  Utils.makeNumCipher(arriveDate.getHours().toString(),2)
            this.arrive_time_mm =   Utils.makeNumCipher(arriveDate.getMinutes().toString(),2)

        }
        //복귀시간도 자동계산
		this.onChangeArriveTime()

    }
    /** 도착시간 변경에 따른 복귀시간 계산  * /
    @action onChangeArriveTime=()=>{
        if(this.arrive_time_hh === ''|| this.arrive_time_mm ===''){return}
        const planDateStr = this.selectObj.plan_date

        //도착시간date변환
        const arriveDate = new Date(parseInt(planDateStr.substring(0,4)),parseInt(planDateStr.substring(4,6))-1, parseInt(planDateStr.substring(6,8)))
        let arriveHour = parseInt(this.arrive_time_hh)
        let arriveMin = parseInt(this.arrive_time_mm)
        arriveDate.setHours(arriveHour, arriveMin)

        // 복귀 시간 계산
        const expectDate = new Date(parseInt(planDateStr.substring(0,4)),parseInt(planDateStr.substring(4,6))-1, parseInt(planDateStr.substring(6,8)))
        const expectTimeStr = this.basicExpectTime
        expectDate.setHours( arriveHour + parseInt(expectTimeStr.substring(0,2))  , arriveMin + parseInt(expectTimeStr.substring(2,4)));
        
        this.return_time_hh = Utils.makeNumCipher(expectDate.getHours().toString(),2)
        this.return_time_mm = Utils.makeNumCipher(expectDate.getMinutes().toString(),2)

    }
    */

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);

        if(id === InputMap.startTime.id) {
            // 레거시 로직: 출발시간 변경 시 접안(-50분), 도착(예상운행시간 반영), 복귀(도착+예상운행시간) 계산
            const startMin = ValueMisc.hhmmToMinute(newInput.data.startTime);
            const expectMin = ValueMisc.hhmmToMinute(getExpectRunTime(newInput.data));
            if(startMin !== null) {
                const shprTime = ValueMisc.minuteToHhmm(startMin - 50);
                if(shprTime) {
                    newInput.data[InputMap.shprTime.id] = shprTime;
                    newInput.error[InputMap.shprTime.id] = false;
                }

                let arriveTime = null;
                if(Math.floor(startMin / 60) >= 22) {
                    const nextArriveReq = autoPlanUploadItem?.arriveReqTimeNext;
                    if(IsValid.hhmm4(nextArriveReq)) arriveTime = nextArriveReq;
                    else if(IsValid.hhmm4(newInput.data.arriveTime)) arriveTime = newInput.data.arriveTime;
                    else arriveTime = '0600';
                }
                else if(expectMin !== null) {
                    arriveTime = ValueMisc.minuteToHhmm(startMin + expectMin);
                }

                if(arriveTime) {
                    newInput.data[InputMap.arriveTime.id] = arriveTime;
                    newInput.error[InputMap.arriveTime.id] = false;

                    if(expectMin !== null) {
                        const arriveMin = ValueMisc.hhmmToMinute(arriveTime);
                        const returnTime = ValueMisc.minuteToHhmm(arriveMin + expectMin);
                        if(returnTime) {
                            newInput.data[InputMap.returnTime.id] = returnTime;
                            newInput.error[InputMap.returnTime.id] = false;
                        }
                    }
                }
            }
        }
        else if(id === InputMap.arriveTime.id) {
            // 레거시 로직: 도착시간 변경 시 복귀시간(도착+예상운행시간) 계산
            const arriveMin = ValueMisc.hhmmToMinute(newInput.data.arriveTime);
            const expectMin = ValueMisc.hhmmToMinute(getExpectRunTime(newInput.data));
            if(arriveMin !== null && expectMin !== null) {
                const returnTime = ValueMisc.minuteToHhmm(arriveMin + expectMin);
                if(returnTime) {
                    newInput.data[InputMap.returnTime.id] = returnTime;
                    newInput.error[InputMap.returnTime.id] = false;
                }
            }
        }

        setInput(newInput);
    };

    const onChangeSelect = (id, value) => {
        onChangeValue(id, value === AppWord.ChooseValue ? undefined : value, false);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        // arriveTime이 startTime보다 작거나 같으면 다음날 도착으로 간주
        param.nextDay = ValueMisc.hhmmToMinute(param.arriveTime) <= ValueMisc.hhmmToMinute(param.startTime);

        param.startTime = ValueMisc.hhmmToH_m_s(param.startTime);
        param.arriveTime = ValueMisc.hhmmToH_m_s(param.arriveTime);
        param.shprTime = ValueMisc.hhmmToH_m_s(param.shprTime);
        param.returnTime = ValueMisc.hhmmToH_m_s(param.returnTime);
        
        param.arriveId = autoPlanUploadItem.arriveId;
        param.dcCd = autoPlanUploadItem.dcCd;
        param.planDate = autoPlanUploadItem.planDate;
        param.hocha = hocha;
        param.storeId = autoPlanUploadItem.storeId;
        param.storeApplyCom = autoPlanUploadItem.storeApplyCom;
        /*
        input InputAutoPlanSave {
            arriveId: String!
            dcCd: String!
            planDate: String!
            hocha: Int!
            storeId: String
            storeApplyCom: String
            vehTypeId: String
            nextDay: Boolean
            shprTime: String
            startTime: String
            arriveTime: String
            returnTime: String
            note: String
        }
        */
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const loadingAny = loadingSave;

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='lg'
            onClose={onClickCancel}
            title={autoPlanUploadItem.storeName + ' ' + (isEdit ? '자동배차 정보 수정' : '자동배차 정보 등록')}
        >
            <DialogContent>
                <PlanTable cellSpacing={0} style={{minWidth: 720}}>
                    <PlanTableHead>
                        <StoreRow>
                            <TitleCell>주력/최대</TitleCell>
                            <TitleCell>LO</TitleCell>
                            <TitleCell>Total<br/>Vol.</TitleCell>
                            <TitleCell>N/Y/O</TitleCell>
                            <TitleCell>
                                PBS_ PT15<br/>
                                점포별 CPP
                            </TitleCell>
                            <TitleCell>당일PT마감</TitleCell>
                            <TitleCell>
                                당일PT야간<br/>
                                당일PT오전<br/>
                                당일PT오후
                            </TitleCell>
                            <TitleCell
                                style={{borderLeft:BorderLightBlue}}
                            >
                                동적1
                            </TitleCell>
                            <TitleCell
                                style={{
                                    borderLeft:BorderLightBlue,
                                    borderRight:BorderLightBlue
                                }}
                            >
                                동적2
                            </TitleCell>
                            <TitleCell>
                                운 송 사<br/>
                                상차공간<br/>
                                DIDA
                            </TitleCell>
                            <TitleCell>
                                전체 PT<br/>
                                미출고PT<br/>
                                20-04PT
                            </TitleCell>
                        </StoreRow>
                        <TableRow>
                            <Boxel>
                                <Cell>
                                    {autoPlanUploadItem.mainVehTypeName}/{autoPlanUploadItem.maxVehTypeName}
                                </Cell>
                            </Boxel>
                            <Boxel>
                                <Cell style={LabelStyle.red}>
                                    {autoPlanUploadItem.leftOver || '-'}
                                </Cell>
                            </Boxel>
                            <Boxel>
                                {ValueMisc.float2str(autoPlanUploadItem.totVol)}
                            </Boxel>
                            <Boxel>
                                <Cell>{autoPlanUploadItem.nBulkPt ? autoPlanUploadItem.nBulkPt : '-'}</Cell>
                                <Cell>{autoPlanUploadItem.yBulkPt ? autoPlanUploadItem.yBulkPt : '-'}</Cell>
                                <Cell style={LabelStyle.blue}>{autoPlanUploadItem.oBulkPt ? autoPlanUploadItem.oBulkPt : '-'}</Cell>
                            </Boxel>
                            <Boxel>
                                <Cell style={LabelStyle.blue}>{autoPlanUploadItem.pbsPt15 || '-'}</Cell>
                                <Cell style={{color:'red', fontSize:'0.6rem'}}>
                                    {autoPlanUploadItem.storeCpp || '-'}
                                </Cell>
                            </Boxel>
                            <Boxel>
                                <Cell style={LabelStyle.red}>{autoPlanUploadItem.dayPt18 || '-'}</Cell>
                            </Boxel>
                            <Boxel>
                                <Cell style={LabelStyle.red}>{autoPlanUploadItem.dayPt04 || '-'}</Cell>
                                <Cell style={LabelStyle.red}>{autoPlanUploadItem.dayPt11 || '-'}</Cell>
                                <Cell style={LabelStyle.red}>{autoPlanUploadItem.dayPt15 || '-'}</Cell>
                            </Boxel>
                            <Boxel style={{borderLeft:AppStyle.BorderCCC}}>
                                {
                                    autoPlanUploadItem.bindOne &&
                                    <>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindOne.targetStoreId}</Cell>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindOne.targetHocha.toString().padStart(3,'0')}</Cell>
                                    <Cell>&nbsp;</Cell>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindOne.bindStoreId}</Cell>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindOne.bindHocha.toString().padStart(3,'0')}</Cell>
                                    </>
                                }
                            </Boxel>
                            <Boxel style={{borderLeft:AppStyle.BorderCCC, borderRight:AppStyle.BorderCCC}}>
                                {
                                    autoPlanUploadItem.bindTwo &&
                                    <>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindTwo.targetStoreId}</Cell>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindTwo.targetHocha.toString().padStart(3,'0')}</Cell>
                                    <Cell>&nbsp;</Cell>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindTwo.bindStoreId}</Cell>
                                    <Cell style={LabelStyle.blue}>{autoPlanUploadItem.bindTwo.bindHocha.toString().padStart(3,'0')}</Cell>
                                    </>
                                }
                            </Boxel>
                            <Boxel>
                                <Cell style={LabelStyle.autoIRBigBold}>{autoPlanUploadItem.comName.substring(0,6)}</Cell>
                                <Cell style={{fontSize:'0.6rem'}}>{autoPlanUploadItem.loadMaxCnt || '-'}</Cell>
                                <Cell style={LabelStyle.blue}>{autoPlanUploadItem.didaNote}</Cell>
                            </Boxel>
                            <Boxel>
                                <Cell style={{textAlign:'right', width: '60%'}}>
                                    <Cell>{autoPlanUploadItem.totalPt}</Cell>
                                    <Cell style={resOverPtStyle}>{autoPlanUploadItem.restOverPt}</Cell>
                                    <Cell>{autoPlanUploadItem.nextday04Pt}</Cell>
                                </Cell>
                            </Boxel>
                        </TableRow>
                    </PlanTableHead>
                </PlanTable>
                <Box style={{maxWidth:400, paddingTop:10}}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <VzmPrint
                                label="호차"
                                value={hocha}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <VzmSelector
                                dict={InputMap.vehTypeId}
                                value={input.data.vehTypeId || AppWord.ChooseValue}
                                onChange={onChangeSelect}
                                list={vehTypeList}
                                fullWidth={true}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Time24Picker
                                dict={InputMap.shprTime}
                                value={input.data.shprTime}
                                onChange={onChangeValue}
                                fullWidth={true}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Time24Picker
                                dict={InputMap.startTime}
                                value={input.data.startTime}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Time24Picker
                                dict={InputMap.arriveTime}
                                value={input.data.arriveTime}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Time24Picker
                                dict={InputMap.returnTime}
                                value={input.data.returnTime}
                                onChange={onChangeValue}
                                fullWidth={true}
                                disabled={true}
                            />
                        </Grid>
                    
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
